/**
 * React Hook: Execute SOQL against Salesforce
 *
 * Uses React Query's useMutation to call POST /soql/execute.
 * Automatically uses selectedOrg from auth store.
 *
 * @example
 * ```tsx
 * const { executeSoql, isPending, data, error } = useExecuteSoql();
 * await executeSoql("SELECT Id, Name FROM Account LIMIT 10");
 * // data.success, data.records, data.totalSize, data.error
 * ```
 */

import { useMutation } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { executeSoql as executeSoqlService } from '@/services/text-to-soql';
import type { SoqlExecuteResponse } from '@/services/text-to-soql';

export interface ExecuteSoqlInput {
  query: string;
  /** SEC-11: the user accepted the sensitive-data warning for this query. */
  confirmSensitive?: boolean;
}

export function useExecuteSoql() {
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const userId = useAuthStoreV1((state) => state.user?.id);

  const mutation = useMutation<SoqlExecuteResponse, Error, string | ExecuteSoqlInput>({
    mutationFn: async (input) => {
      if (!selectedOrg) {
        throw new Error('No organization selected');
      }
      const { query, confirmSensitive = false } =
        typeof input === 'string' ? { query: input } : input;
      return executeSoqlService(query.trim(), selectedOrg, userId, confirmSensitive);
    },
    onError: (error) => {
      console.error('❌ Failed to execute SOQL:', error);
    },
  });

  return {
    executeSoql: mutation.mutateAsync,
    isPending: mutation.isPending,
    data: mutation.data ?? null,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
